/**
 * LayoutElementsPalette — F12.H T12.H.10 + F12.N D2 drag&drop.
 *
 * Paleta layout elementów w left panelu kreatora modułu. Lista typów
 * pogrupowana po kategoriach z `LayoutElementRegistry.getByCategory()`.
 *
 * Dwa tryby interakcji:
 *  - **klik** → `onAdd(type)` → `addLayoutElementToCanvas` w buildery (default).
 *  - **drag&drop** (F12.N D2) → admin może przeciągnąć klocek na konkretną sekcję
 *    canvasu. `useDraggable` z `data: PaletteDragData` — top-level DndContext
 *    w `ModuleBuilderPage` rozpoznaje `source === 'palette'` w `usePaletteDnD`.
 *
 * Aktywacja DnD: 5px distance (sensors z `usePaletteDnD`) — krótki klik bez ruchu
 * wywoła nadal `onAdd` (oba mechanizmy współistnieją).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDraggable } from '@dnd-kit/core';
import {
  Boxes,
  LayoutDashboard,
  MessageSquare,
  MousePointer,
  Type as TypeIcon,
} from 'lucide-react';

import {
  type LayoutElementDefinition,
  LayoutElementRegistry,
} from '@/components/fields/LayoutElementRegistry';
import { Tooltip } from '@/components/Tooltip';
import type { PaletteDragData } from '@/hooks/usePaletteDnD';
import { normalizeSearch } from '@/lib/search-normalize';

import { PaletteSearchInput } from './PaletteSearchInput';

type Category = NonNullable<LayoutElementDefinition['category']>;

const CATEGORY_META: Record<Category, { label: string; icon: typeof Boxes; order: number }> = {
  struktura: { label: 'Struktura strony', icon: LayoutDashboard, order: 1 },
  kontenery: { label: 'Kontenery', icon: Boxes, order: 2 },
  interaktywne: { label: 'Interaktywne', icon: MousePointer, order: 3 },
  tresc: { label: 'Treść', icon: TypeIcon, order: 4 },
  komunikaty: { label: 'Komunikaty', icon: MessageSquare, order: 5 },
};

interface LayoutElementsPaletteProps {
  /** Callback wywoływany przy kliknięciu w element palety. */
  onAdd: (type: string) => void;
  /**
   * Slug modułu systemowego, którego layout edytuje ten builder (np.
   * `purchase_invoices`) — używany do filtrowania elementów ze scopingiem
   * (`LayoutElementDefinition.allowedModules`, patrz `document_items_view`).
   * Elementy BEZ `allowedModules` są widoczne zawsze, niezależnie od tego propa.
   * Elementy Z `allowedModules` wymagają `moduleSlug` w liście — brak propa
   * (builder bez znajomości modułu, np. kreator modułów własnych) je ukrywa,
   * co jest bezpiecznym domyślnym zachowaniem.
   */
  moduleSlug?: string;
  /**
   * Typy klocków do wykluczenia z palety, niezależnie od `allowedModules`.
   * Użycie: kontekst gdzie klocek NIE ma żadnej ścieżki do `propertiesEditor`
   * (np. `tab.extras` dla zakładek non-fields, renderowane przez
   * `NonFieldsExtrasPanel` w `SystemModuleBuilderPage.tsx` — tylko podgląd +
   * usuń, bez wejścia w edytor właściwości). Klocek wymagający wyboru
   * tabeli/widoku (`additional_view`/`additional_query_view`) wpuszczony tam
   * nigdy nie dostałby wartości i renderowałby się jako `null` na realnej
   * kartotece — cicho, bez ostrzeżenia.
   */
  excludeTypes?: string[];
}

export function LayoutElementsPalette({
  onAdd,
  moduleSlug,
  excludeTypes,
}: LayoutElementsPaletteProps) {
  const { t } = useTranslation();
  // Wyszukiwarka elementów (parytet z paletą kartotek w MenuPalette) — filtr po
  // nazwie/typie z normalizeSearch (diakrytyka-insensitive). Grupa (kategoria) znika
  // gdy 0 trafień. Drag&drop i grupowanie bez zmian.
  const [query, setQuery] = useState('');

  // Zbierz wszystkie zarejestrowane elementy (odfiltrowane wg scopingu modułu,
  // z jawnym wykluczeniem typów, oraz z pominięciem elementów wycofanych z palety —
  // `hiddenFromPalette`, np. `page-navbar`; wciąż zarejestrowane w registry, żeby stare
  // zapisane layouty nadal się renderowały).
  const all = LayoutElementRegistry.getAll().filter(
    (def) =>
      !def.hiddenFromPalette &&
      (!def.allowedModules || (moduleSlug != null && def.allowedModules.includes(moduleSlug))) &&
      !excludeTypes?.includes(def.type),
  );

  // Filtr wyszukiwarki po nazwie/typie, potem grupowanie po kategorii — kategoria
  // z 0 trafień nie trafia do mapy, więc znika z UI.
  const q = normalizeSearch(query.trim());
  const filtered = q
    ? all.filter((def) => normalizeSearch(def.label ?? def.type).includes(q))
    : all;
  const byCategory = new Map<Category, LayoutElementDefinition[]>();
  for (const def of filtered) {
    if (!def.category) continue;
    const arr = byCategory.get(def.category) ?? [];
    arr.push(def);
    byCategory.set(def.category, arr);
  }

  // Sortuj kategorie po order
  const categories = Array.from(byCategory.entries()).sort(
    ([a], [b]) => (CATEGORY_META[a]?.order ?? 99) - (CATEGORY_META[b]?.order ?? 99),
  );

  if (all.length === 0) {
    return (
      <p className="text-xs text-text-muted" data-testid="palette-empty">
        Brak zarejestrowanych elementów layoutu.
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="layout-elements-palette">
      <PaletteSearchInput
        value={query}
        onChange={setQuery}
        placeholder={t('common.layoutPalette.searchElements')}
        testId="layout-elements-palette-search"
      />
      {categories.length === 0 ? (
        <p className="text-xs text-text-muted" data-testid="layout-elements-palette-no-results">
          {t('common.layoutPalette.noElements')}
        </p>
      ) : null}
      {categories.map(([category, defs]) => {
        const meta = CATEGORY_META[category];
        const CategoryIcon = meta?.icon ?? Boxes;
        return (
          <div key={category}>
            <div className="mb-1.5 flex items-center gap-1.5">
              <CategoryIcon className="h-3 w-3 text-text-muted" aria-hidden="true" />
              <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                {meta?.label ?? category}
              </p>
              <span className="text-xs text-text-muted">({defs.length})</span>
            </div>
            <div className="space-y-1">
              {defs.map((def) => (
                <PaletteItem key={def.type} def={def} onAdd={() => onAdd(def.type)} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Pojedynczy item w palecie ────────────────────────────────────────────────

function PaletteItem({ def, onAdd }: { def: LayoutElementDefinition; onAdd: () => void }) {
  const { t } = useTranslation();
  const dragData: PaletteDragData = {
    source: 'palette',
    kind: 'layout-element',
    payload: def.type,
  };
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-element-${def.type}`,
    data: dragData,
  });

  const label = def.label ?? def.type;
  // Użytkowy opis elementu (co robi + kiedy użyć), keyowany po typie. Brak wpisu →
  // '' → w tooltipie pokazujemy samą nazwę (T7). Klucze: paletteDescriptions.<type>.
  const desc = t(`tenant.systemModuleBuilder.paletteDescriptions.${def.type}`, {
    defaultValue: '',
  });

  return (
    <Tooltip
      placement="right"
      tone="info"
      content={
        <span>
          <b>{label}</b>
          {desc ? (
            <>
              <br />
              {desc}
            </>
          ) : null}
        </span>
      }
    >
      <button
        ref={setNodeRef}
        type="button"
        onClick={onAdd}
        {...attributes}
        {...listeners}
        className={`flex w-full items-center gap-2 rounded-md border border-border-primary bg-bg-primary px-2 py-1.5 text-left text-xs text-text-primary transition-colors hover:border-accent-400 hover:bg-accent-50 ${
          isDragging ? 'cursor-grabbing opacity-50' : 'cursor-grab'
        }`}
        data-testid={`palette-item-${def.type}`}
      >
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-bg-secondary text-text-muted">
          {/* Ikona placeholder — pełen Lucide lookup w T12.H.9 panel właściwości */}
          <Boxes className="h-3 w-3" aria-hidden="true" />
        </span>
        <span className="flex-1 truncate font-medium">{label}</span>
      </button>
    </Tooltip>
  );
}
